import { Box, Flex, PrimaryButton } from "@nulogy/components";
import IndexTable from "components/common/IndexTable";
import UnstyledLink from "components/common/UnstyledLink";
import { formatDateTime } from "components/helpers/format";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import paths from "config/routePaths";
import omit from "lodash/omit";
import React from "react";

const ROLE_LABELS = {
  nulogy_admin: "Nulogy Admin",
  nulogy_restricted: "Nulogy Restricted",
  planner: "Planner",
  readonly: "Read-only",
};

function renderColumns(canUpdateCompany) {
  return [
    {
      cellRenderer: ({ row }) => companyCellRenderer({ canUpdateCompany, row }),
      dataKey: "companyName",
      label: "Company name",
      numeric: true,
    },
    { cellRenderer: emailCellRenderer, dataKey: "email", label: "User email", numeric: true },
    { cellFormatter: labelForRole, dataKey: "role", label: "Role" },
    { cellFormatter: formatTime, dataKey: "createdAt", label: "Created" },
    { cellFormatter: formatTime, dataKey: "updatedAt", label: "Last updated" },
  ];
}

function labelForRole({ cellData: role }) {
  const label = ROLE_LABELS[role];
  if (!label) throw new Error(`Unhandled role: ${role}`);

  return label;
}

function formatTime({ cellData }) {
  return formatDateTime(cellData);
}

function companyCellRenderer({ row, canUpdateCompany }) {
  if (canUpdateCompany) {
    return (
      <UnstyledLink className="spec-company-name" href={paths.companyEdit(row.companyId)}>
        {row.companyName}
      </UnstyledLink>
    );
  }

  return <div className="spec-company-name">{row.companyName}</div>;
}

function emailCellRenderer({ row }) {
  return <UnstyledLink href={paths.userEdit(row.id)}>{row.email}</UnstyledLink>;
}

export default function UserIndexView({ users, canCreateUser, canUpdateCompany, createUserPath, simpleView }) {
  const flattenedUsers = users.map((user) => ({
    ...omit(user, "allowedSites"),
    companyId: user.company?.id,
    companyName: user.company?.name,
  }));

  return (
    <PageWithBreadcrumbs data-testid="user-page" simpleView={simpleView} title="Users">
      <Flex className="spec-user-index-page">
        {canCreateUser && (
          <Box>
            <PrimaryButton asLink href={createUserPath} marginTop="x1" size="small" type="button">
              Create user
            </PrimaryButton>
          </Box>
        )}
      </Flex>
      <IndexTable
        columns={renderColumns(canUpdateCompany)}
        initialSortColumn="email"
        keyField="id"
        rows={flattenedUsers}
      />
    </PageWithBreadcrumbs>
  );
}
